import React from 'react';
import styled, { css } from 'styled-components';
import Container from '../container';
import LazyImage from '../lazy-image';
import { getFluidGatsbyImage } from '../../utils/gatsby-storyblok-image-fixed';
import Button from '../button';
import parseStoryblokLink from '../../utils/parse-storyblok-link';
import RichtextParser from '../../utils/richtext-parser';
import { styledLink } from '../../utils/styles';
import mq from '../../utils/mq';

const OuterContentGridContainer = styled.div<{ $theme?: 'white' | 'grey' }>`
  background-color: ${({ $theme }) =>
    $theme === 'white' ? '#fff' : '#f8f8f8'};
  padding-top: 104px;
  padding-bottom: 104px;
  ${mq.mobile(css`
    padding-top: 64px;
    padding-bottom: 64px;
  `)};
`;

const ContentGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  gap: 20px;
  padding-bottom: 80px;
  ${mq.mobile(css`
    padding-bottom: 40px;
  `)};
`;

const TitleContainer = styled.div`
  grid-column: 3 / span 8;
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  text-align: center;
  ${mq.mobile(css`
    grid-column: 1 / span 12;
    text-align: left;
    align-items: flex-start;
    gap: 16px;
  `)};
`;

const Title = styled.h2`
  color: #161616;
  font-size: 42px;
  font-style: normal;
  font-weight: 700;
  line-height: 115%;
  letter-spacing: -0.84px;
  text-align: inherit;
  max-width: 536px;
  ${mq.mobile(css`
    font-size: 36px;
  `)};
`;

const Description = styled.div`
  color: var(--Foreground-fg-secondary, #565656);
  font-size: 18px;
  font-weight: 400;
  line-height: 160%;
  text-wrap: pretty;
  text-align: inherit;
  max-width: 536px;
  p {
    color: #666 !important;
  }
  a {
    ${styledLink};
    text-decoration: none;
  }
`;

const ContentGridItems = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  grid-gap: 80px;
  width: 100%;
  @media (max-width: 1100px) {
    grid-gap: 40px;
  }
  ${mq.mobile(css`
    grid-template-columns: repeat(1, 1fr);
    grid-gap: 32px;
  `)};
`;

const ContentGridItem = styled.div`
  width: 100%;
`;

const ContentGridItemImage = styled.div<{ $theme?: 'white' | 'grey' }>`
  aspect-ratio: 3/2;
  width: 100%;
  margin-bottom: 32px;
  overflow: hidden;
  position: relative;
  background: ${({ $theme }) =>
    $theme === 'white' ? 'var(--Color-Grey-1, #DEE2E4)' : '#fff'};
  border-radius: var(--Radius-Radius-3, 16px);
  ${mq.mobile(css`
    margin-bottom: 24px;
  `)};
`;

const ContentGridItemTitle = styled.h3`
  color: #161616;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 125%;
  padding-bottom: 20px;
  ${mq.mobile(css`
    padding-bottom: 8px;
    font-size: 22px;
  `)};
`;

const ContentGridItemDescription = styled.div`
  color: #666;
  font-size: 18px;
  font-weight: 400;
  line-height: 160%;
  a {
    ${styledLink};
    text-decoration: none;
  }
`;

const CtaContainer = styled.div`
  padding-top: 80px;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  ${mq.mobile(css`
    padding-top: 32px;
  `)};
`;

export default function ContentGrid({
  sectionTitle,
  sectionDescription,
  gridItems,
  ctaLabel,
  ctaLink,
  theme
}: {
  sectionTitle: string;
  sectionDescription: string;
  gridItems: any[];
  ctaLabel: string;
  ctaLink: string;
  theme?: 'white' | 'grey';
}) {
  return (
    <OuterContentGridContainer $theme={theme}>
      <Container>
        <ContentGridContainer>
          <TitleContainer>
            <Title>{sectionTitle}</Title>
            <Description
              dangerouslySetInnerHTML={{
                __html: RichtextParser(sectionDescription)
              }}
            />
          </TitleContainer>
        </ContentGridContainer>
        <ContentGridItems>
          {gridItems.map((item) => (
            <ContentGridItem key={item.id}>
              <ContentGridItemImage $theme={theme}>
                <LazyImage
                  fluid={getFluidGatsbyImage(item.image)}
                  width="100%"
                  height="100%"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </ContentGridItemImage>
              <ContentGridItemTitle>{item.title}</ContentGridItemTitle>
              <ContentGridItemDescription
                dangerouslySetInnerHTML={{
                  __html: RichtextParser(item.description)
                }}
              />
            </ContentGridItem>
          ))}
        </ContentGridItems>
        {ctaLink && ctaLabel && (
          <CtaContainer>
            <Button to={parseStoryblokLink(ctaLink)}>{ctaLabel}</Button>
          </CtaContainer>
        )}
      </Container>
    </OuterContentGridContainer>
  );
}
